import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { CameraLogEntry, CareKind, NurseryBoard } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { CameraViewer } from './CameraViewer';
import { ageRange, CARE_LABEL, clock, formatAge } from './format';

type Stay = NurseryBoard['rooms'][number]['stays'][number];

/** Lo que ve el personal: quién está en cada sala, recibir, registrar su cuidado y entregar. */
export function StaffBoard({ churchId, board, onChange }: { churchId: string; board: NurseryBoard; onChange: () => void }) {
  return (
    <>
      <StaffCheckIn churchId={churchId} board={board} onDone={onChange} />
      {board.rooms.length === 0 ? <EmptyState title="La guardería aún no tiene salas." /> : null}
      {board.rooms.map((room) => (
        <Card
          key={room.id}
          title={
            <>
              {room.name} <span className="muted small">· {ageRange(room.minAgeMonths, room.maxAgeMonths)}</span>
            </>
          }
          action={<span className="badge">{room.capacity ? `${room.stays.length} / ${room.capacity}` : room.stays.length}</span>}
        >
          {room.stays.length === 0 ? <p className="muted small">Sin niños ahora.</p> : null}
          <ul className="list">
            {room.stays.map((s) => (
              <StayRow key={s.id} churchId={churchId} stay={s} canRelease={board.canRelease} onChange={onChange} />
            ))}
          </ul>
          {room.cameras.map((c) => (
            <CameraViewer key={c.id} churchId={churchId} cameraId={c.id} name={c.name} />
          ))}
          {board.canManage ? <RoomAdmin churchId={churchId} roomId={room.id} cameras={room.cameras} camerasEnabled={board.camerasEnabled} onChange={onChange} /> : null}
        </Card>
      ))}
      {board.canManage ? (
        <>
          <NewRoom churchId={churchId} onDone={onChange} />
          {board.camerasEnabled ? <CameraLog churchId={churchId} /> : null}
        </>
      ) : null}
    </>
  );
}

function StaffCheckIn({ churchId, board, onDone }: { churchId: string; board: NurseryBoard; onDone: () => void }) {
  const [childId, setChildId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const child = board.children.find((c) => c.personId === childId);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/nursery/churches/${churchId}/stays`, { childId, roomId: String(form.get('roomId')), droppedOffBy: String(form.get('droppedOffBy')) });
      setChildId('');
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (board.children.length === 0 || board.rooms.length === 0) return null;
  return (
    <Card title="Recibir a un niño">
      <form className="form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="staff-child">Niño o niña</label>
          <select id="staff-child" value={childId} onChange={(e) => setChildId(e.target.value)} required>
            <option value="">Elige…</option>
            {board.children.map((c) => (
              <option key={c.personId} value={c.personId}>
                {c.number ? `#${c.number} · ` : ''}{c.name} {c.ageMonths !== null ? `(${formatAge(c.ageMonths)})` : ''}
              </option>
            ))}
          </select>
        </div>
        {child ? (
          <>
            {child.allergies ? <Alert>Alergias: {child.allergies}</Alert> : null}
            <div className="form__row">
              <div className="field">
                <label htmlFor="staff-guardian">Lo entrega</label>
                <select id="staff-guardian" name="droppedOffBy" required>
                  {child.guardians.map((g) => (
                    <option key={g.personId} value={g.personId}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="staff-room">Sala</label>
                <select id="staff-room" name="roomId" required>
                  {board.rooms.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Button type="submit" busy={busy}>
              Registrar entrada
            </Button>
          </>
        ) : null}
        {error ? <Alert>{error}</Alert> : null}
      </form>
    </Card>
  );
}

function StayRow({ churchId, stay, canRelease, onChange }: { churchId: string; stay: Stay; canRelease: boolean; onChange: () => void }) {
  const [panel, setPanel] = useState<'event' | 'release' | null>(null);
  return (
    <li className="nursery-stay">
      <div className="nursery-stay__head">
        <span>
          {stay.child.number ? <span className="badge">#{stay.child.number}</span> : null} <strong>{stay.child.name}</strong> <span className="muted small">· {formatAge(stay.child.ageMonths)} · desde {clock(stay.checkedInAt)} · lo trajo {stay.droppedOffBy}</span>
        </span>
        <span className="actions">
          <Button type="button" variant="secondary" aria-label={`Anotar (comida, pañal…) de ${stay.child.name}`} onClick={() => setPanel(panel === 'event' ? null : 'event')}>
            Anotar (comida, pañal…)
          </Button>
          <CallParents churchId={churchId} stay={stay} onChange={onChange} />
          {canRelease ? (
            <Button type="button" onClick={() => setPanel(panel === 'release' ? null : 'release')}>
              Entregar
            </Button>
          ) : null}
        </span>
      </div>
      {stay.allergies ? <p className="allergy">Alergias: {stay.allergies}</p> : null}
      {stay.instructions ? <p className="small">{stay.instructions}</p> : null}
      {stay.medical ? <p className="small">Medicamentos o condiciones: {stay.medical}</p> : null}
      {stay.contactPhone || stay.diaper || stay.photoConsent === false ? (
        <p className="small muted">
          {[stay.contactPhone ? `Tel. ${stay.contactPhone}` : null, stay.diaper ? 'Usa pañal' : null, stay.photoConsent === false ? 'Sin permiso de fotos' : null].filter(Boolean).join(' · ')}
        </p>
      ) : null}
      {stay.events.length > 0 ? (
        <p className="small muted">{stay.events.map((e) => `${CARE_LABEL[e.kind]} ${clock(e.recordedAt)}`).join(' · ')}</p>
      ) : null}
      {panel === 'event' ? <CareForm churchId={churchId} stayId={stay.id} onDone={() => { setPanel(null); onChange(); }} /> : null}
      {panel === 'release' ? <ReleaseForm churchId={churchId} stay={stay} onDone={() => { setPanel(null); onChange(); }} /> : null}
    </li>
  );
}

function CareForm({ churchId, stayId, onDone }: { churchId: string; stayId: string; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/nursery/churches/${churchId}/stays/${stayId}/events`, { kind: String(form.get('kind')) as CareKind, note: String(form.get('note') ?? '') });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <form className="form panel" onSubmit={submit}>
      <div className="form__row">
        <div className="field">
          <label htmlFor={`kind-${stayId}`}>Qué pasó</label>
          <select id={`kind-${stayId}`} name="kind">
            {Object.entries(CARE_LABEL).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`note-${stayId}`}>Nota</label>
          <input id={`note-${stayId}`} name="note" maxLength={500} />
        </div>
      </div>
      <p className="small muted">Un incidente o "Llamar al tutor" avisa de inmediato a sus tutores.</p>
      {error ? <Alert>{error}</Alert> : null}
      <Button type="submit" variant="secondary" busy={busy}>
        Guardar
      </Button>
    </form>
  );
}

/** Entregar exige el código, a una persona autorizada y verificar su identificación. */
function ReleaseForm({ churchId, stay, onDone }: { churchId: string; stay: Stay; onDone: () => void }) {
  const options = useAsync(
    (signal) => api.get<{ guardians: { personId: string; name: string }[]; pickups: { id: string; name: string; relationship: string }[] }>(`/nursery/churches/${churchId}/stays/${stay.id}/release-options`, signal),
    [stay.id],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const [kind, id] = String(form.get('pickup') ?? '').split(':');
    if (!kind || !id) {
      setError('Elige quién lo recoge.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/nursery/churches/${churchId}/stays/${stay.id}/release`, {
        code: String(form.get('code')),
        pickup: kind === 'guardian' ? { kind, personId: id } : { kind: 'authorized', pickupId: id },
        identityVerified: form.get('verified') === 'on',
      });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <form className="form panel" onSubmit={submit}>
      <div className="field">
        <label htmlFor={`code-${stay.id}`}>Código del tutor (escanea el QR o escríbelo)</label>
        <input id={`code-${stay.id}`} name="code" required autoComplete="off" autoCapitalize="characters" maxLength={12} className="code-input" />
      </div>
      <fieldset className="choices">
        <legend>¿Quién lo recoge?</legend>
        {options.status === 'ready' ? (
          <>
            {options.data.guardians.map((g) => (
              <label key={g.personId} className="choice">
                <input type="radio" name="pickup" value={`guardian:${g.personId}`} />
                <span>
                  {g.name} <span className="muted small">· tutor</span>
                </span>
              </label>
            ))}
            {options.data.pickups.map((p) => (
              <label key={p.id} className="choice">
                <input type="radio" name="pickup" value={`authorized:${p.id}`} />
                <span>
                  {p.name} <span className="muted small">· {p.relationship}, autorizada</span>
                </span>
              </label>
            ))}
          </>
        ) : (
          <p className="muted small">Cargando…</p>
        )}
      </fieldset>
      <label className="choice">
        <input type="checkbox" name="verified" />
        <span>Verifiqué su identificación con foto</span>
      </label>
      <p className="small muted">Si quien llega no aparece en la lista, no se entrega: llama a un tutor.</p>
      {error ? <Alert>{error}</Alert> : null}
      <Button type="submit" busy={busy}>
        Confirmar entrega
      </Button>
    </form>
  );
}

function NewRoom({ churchId, onDone }: { churchId: string; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const num = (key: string) => (form.get(key) ? Number(form.get(key)) : null);
    setBusy(true);
    setError(null);
    try {
      const years = (key: string) => (num(key) === null ? null : num(key)! * 12);
      await api.post(`/nursery/churches/${churchId}/rooms`, { name: String(form.get('name')), minAgeMonths: years('minYears'), maxAgeMonths: years('maxYears') === null ? null : years('maxYears')! + 11, capacity: num('capacity') });
      formEl.reset();
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Nueva sala">
      <form className="form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="room-name">Nombre</label>
          <input id="room-name" name="name" required maxLength={80} placeholder="Párvulos" />
        </div>
        <div className="form__row">
          <div className="field">
            <label htmlFor="room-min">Desde (años)</label>
            <input id="room-min" name="minYears" type="number" min={0} max={17} inputMode="numeric" />
          </div>
          <div className="field">
            <label htmlFor="room-max">Hasta (años)</label>
            <input id="room-max" name="maxYears" type="number" min={0} max={17} inputMode="numeric" />
          </div>
        </div>
        <div className="field">
          <label htmlFor="room-capacity">Cupo</label>
          <input id="room-capacity" name="capacity" type="number" min={1} max={500} inputMode="numeric" />
        </div>
        {error ? <Alert>{error}</Alert> : null}
        <Button type="submit" variant="secondary" busy={busy}>
          Crear sala
        </Button>
      </form>
    </Card>
  );
}

function RoomAdmin({ churchId, roomId, cameras, camerasEnabled, onChange }: { churchId: string; roomId: string; cameras: { id: string; name: string }[]; camerasEnabled: boolean; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function run(key: string, action: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await action();
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  if (!open) {
    return (
      <Button type="button" variant="ghost" onClick={() => setOpen(true)}>
        Administrar sala
      </Button>
    );
  }
  return (
    <div className="panel">
      {camerasEnabled ? (
        <>
          {cameras.map((c) => (
            <div key={c.id} className="list__item">
              <span>{c.name}</span>
              <Button type="button" variant="ghost" busy={busy === c.id} onClick={() => { if (window.confirm(`¿Quitar la cámara «${c.name}»? Los papás ya no podrán verla.`)) run(c.id, () => api.delete(`/nursery/churches/${churchId}/cameras/${c.id}`)); }}>
                Quitar cámara
              </Button>
            </div>
          ))}
          <form
            className="form form--inline"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              void run('camera', () => api.post(`/nursery/churches/${churchId}/rooms/${roomId}/cameras`, { name: String(form.get('name')), source: String(form.get('source')) }));
            }}
          >
            <div className="field">
              <label htmlFor={`cam-name-${roomId}`}>Cámara</label>
              <input id={`cam-name-${roomId}`} name="name" required maxLength={80} placeholder="Vista general" />
            </div>
            <div className="field">
              <label htmlFor={`cam-source-${roomId}`}>Referencia en el sistema de cámaras</label>
              <input id={`cam-source-${roomId}`} name="source" required maxLength={200} />
            </div>
            <Button type="submit" variant="secondary" busy={busy === 'camera'}>
              Agregar cámara
            </Button>
          </form>
        </>
      ) : (
        <p className="muted small">Las cámaras no están configuradas en este servidor.</p>
      )}
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions actions--wrap">
        <Button type="button" variant="danger" busy={busy === 'archive'} onClick={() => { if (window.confirm('¿Archivar esta sala? Ya no aparecerá para registrar niños.')) run('archive', () => api.delete(`/nursery/churches/${churchId}/rooms/${roomId}`)); }}>
          Archivar sala
        </Button>
        <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
          Listo
        </Button>
      </div>
    </div>
  );
}

function CameraLog({ churchId }: { churchId: string }) {
  const log = useAsync((signal) => api.get<{ items: CameraLogEntry[] }>(`/nursery/churches/${churchId}/camera-log`, signal).then((r) => r.items), [churchId]);
  return (
    <Card title="Registro de acceso a cámaras">
      {log.status === 'ready' && log.data.length === 0 ? <p className="muted small">Nadie ha visto las cámaras.</p> : null}
      {log.status === 'ready' ? (
        <ul className="list">
          {log.data.map((g) => (
            <li key={g.id} className="list__item">
              <span>
                <strong>{g.person}</strong> <span className="muted small">· {g.camera} · {g.basis === 'guardian' ? 'tutor' : 'personal'}</span>
              </span>
              <span className="small muted">
                {new Date(g.grantedAt).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {g.views === 1 ? '1 imagen' : `${g.views} imágenes`}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

/** Llama a los papás: les llega aviso al celular y sale en las pantallas el número del niño. */
function CallParents({ churchId, stay, onChange }: { churchId: string; stay: Stay; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function call() {
    if (!window.confirm(`¿Llamar a los papás de ${stay.child.name}? Les llegará un aviso y saldrá en las pantallas${stay.child.number ? ` el número #${stay.child.number}` : ''}.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/nursery/churches/${churchId}/stays/${stay.id}/events`, { kind: 'call_guardian', note: null });
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button type="button" variant="danger" busy={busy} onClick={() => void call()}>
        Llamar a papás
      </Button>
      {error ? <Alert>{error}</Alert> : null}
    </>
  );
}
